import React, { useState, useEffect, useRef, useCallback } from "react";
import QRCode from "qrcode";
import "./financingplus.css";
import {
  CHANNELS,
  STORE_KEY,
  getBannerHTML,
  getHeroHTML,
  getInfoHTML,
  getFooterHTML,
  getSigBoldHTML,
  getSigMinimalHTML,
  getEmailTplHTML,
  smsBodies,
  captionTexts,
  printHTML,
  guidesFor,
  normalizeGoodLink,
  isDemoSavedData,
  wrapText,
  disclosureShort,
} from "./generators";

const ASSET = "/assets/financingplus";
const LOGO_WHITE = `${ASSET}/logos/goodleap-white.svg`;
const LOGO_PARTNER = `${ASSET}/logos/partners/srs-white.png`;
const LOGO_HUB = `${ASSET}/logos/partners/roofhub-white.svg`;
const HERO_BG = `${ASSET}/backgrounds/hero-composite.png`;
const ICON = (name) => `${ASSET}/icons/${name}.svg`;

const DEFAULTS = {
  partnerName: "SRS Distribution",
  showPartnerLogo: true,
  hubName: "Roof Hub",
  defaultChannel: "website",
  includeSchema: true,
  includeFontLink: true,
};

function btnClass(generated, copied, kind = "primary") {
  const parts = ["fp-btn", kind === "secondary" ? "fp-btn--secondary" : "fp-btn--primary"];
  if (!generated) parts.push("fp-btn--disabled");
  if (copied) parts.push("fp-btn--copied");
  return parts.join(" ");
}

function ArrowChip() {
  return (
    <svg width="21" height="21" viewBox="0 0 21 21" fill="none" aria-hidden="true" style={{ display: "block" }}>
      <path d="M3.5 10.5H19.25" stroke="currentColor" strokeWidth="1.5" />
      <path d="M12.133 3.813L18.818 10.498L12.133 17.183" stroke="currentColor" strokeWidth="1.5" fill="none" />
    </svg>
  );
}

function stripPreviewHtml(html) {
  return String(html || "")
    .replace(/<!--[\s\S]*?-->/g, "")
    .replace(/<link[^>]*>/gi, "");
}

function PreviewHtml({ html, style }) {
  return (
    <div
      style={style}
      dangerouslySetInnerHTML={{ __html: stripPreviewHtml(html) }}
    />
  );
}

function FinancingPlus(props = {}) {
  const partnerName = props.partnerName ?? DEFAULTS.partnerName;
  const showPartnerLogo = props.showPartnerLogo ?? DEFAULTS.showPartnerLogo;
  const hubName = props.hubName ?? DEFAULTS.hubName;
  const defaultChannel = props.defaultChannel ?? DEFAULTS.defaultChannel;
  const includeSchema = props.includeSchema ?? DEFAULTS.includeSchema;
  const includeFontLink = props.includeFontLink ?? DEFAULTS.includeFontLink;
  const genOpts = { includeSchema, includeFontLink };

  const [nameInput, setNameInput] = useState("");
  const [linkInput, setLinkInput] = useState("");
  const [appliedName, setAppliedName] = useState("Your company");
  const [appliedLink, setAppliedLink] = useState("https://financing.goodleap.plus/your-link");
  const [channel, setChannel] = useState(null);
  const [generated, setGenerated] = useState(false);
  const [copiedKey, setCopiedKey] = useState("");
  const [openGuide, setOpenGuide] = useState("");
  const [qrSvg, setQrSvg] = useState("");

  const squareRef = useRef(null);
  const portraitRef = useRef(null);
  const logoImgRef = useRef(null);
  const copiedTimerRef = useRef(null);
  const hydratedRef = useRef(false);
  const drawSocialLatestRef = useRef(null);

  const active = channel || defaultChannel;
  const fieldsReady = Boolean(nameInput.trim() && linkInput.trim());

  const persist = useCallback((name, link, ch) => {
    try {
      window.localStorage.setItem(
        STORE_KEY,
        JSON.stringify({
          name: name ?? nameInput,
          link: link ?? linkInput,
          channel: ch !== undefined ? ch : channel,
        })
      );
    } catch (e) {
      /* storage unavailable */
    }
  }, [nameInput, linkInput, channel]);

  const updateQr = useCallback(async (link) => {
    const url = link || appliedLink;
    try {
      const svg = await QRCode.toString(url, {
        type: "svg",
        width: 240,
        margin: 2,
        color: { dark: "#200F51", light: "#FFFFFF" },
      });
      setQrSvg(svg);
    } catch (e) {
      setQrSvg("");
    }
  }, [appliedLink]);

  const drawSocial = useCallback(() => {
    if ((channel || defaultChannel) !== "social") return;
    if (!logoImgRef.current) {
      const img = new Image();
      img.onload = () => {
        if (drawSocialLatestRef.current) drawSocialLatestRef.current();
      };
      img.src = LOGO_WHITE;
      logoImgRef.current = img;
    }
    const logoImg = logoImgRef.current;
    [[squareRef, 1080, 1080], [portraitRef, 1080, 1350]].forEach(([ref, w, h]) => {
      const cv = ref.current;
      if (!cv) return;
      cv.width = w;
      cv.height = h;
      const ctx = cv.getContext("2d");
      const D = "500 Npx 'Roc Grotesk','Montserrat',sans-serif";
      const B = "Npx 'Sofia Pro','Nunito',sans-serif";
      const f = (tpl, size) => tpl.replace("N", size);

      const g = ctx.createLinearGradient(0, 0, w, h);
      g.addColorStop(0, "#4A2DAB");
      g.addColorStop(1, "#200F51");
      ctx.fillStyle = g;
      ctx.fillRect(0, 0, w, h);

      const glow = ctx.createRadialGradient(w * 0.86, h * 0.1, 0, w * 0.86, h * 0.1, w * 0.8);
      glow.addColorStop(0, "rgba(247,179,52,0.42)");
      glow.addColorStop(0.35, "rgba(202,182,255,0.20)");
      glow.addColorStop(1, "rgba(32,15,81,0)");
      ctx.fillStyle = glow;
      ctx.fillRect(0, 0, w, h);

      ctx.save();
      ctx.globalAlpha = 0.2;
      ctx.fillStyle = "#FFFEFC";
      for (let y = h - 160; y < h; y += 13) {
        for (let x = 0; x < w; x += 13) ctx.fillRect(x, y, 2, 2);
      }
      ctx.restore();

      const pad = 88;
      let y = pad + 40;
      ctx.textBaseline = "alphabetic";
      if ("letterSpacing" in ctx) ctx.letterSpacing = "4px";
      ctx.font = f(D, 24);
      ctx.fillStyle = "#D1C1FF";
      ctx.fillText(String(appliedName).toUpperCase(), pad, y);
      if ("letterSpacing" in ctx) ctx.letterSpacing = "0px";

      y += h === 1080 ? 110 : 150;
      ctx.font = f(D, 96);
      ctx.fillStyle = "#FFFEFC";
      ctx.fillText("Make your home", pad, y);
      ctx.fillText("project", pad, y + 100);
      const affX = pad + ctx.measureText("project ").width;
      const affG = ctx.createLinearGradient(affX, 0, affX + ctx.measureText("affordable").width, 0);
      affG.addColorStop(0, "#6C51E3");
      affG.addColorStop(0.5, "#D551C9");
      affG.addColorStop(1, "#FF8300");
      ctx.fillStyle = affG;
      ctx.fillText("affordable", affX, y + 100);

      y += 100 + (h === 1080 ? 92 : 130);
      ctx.font = f(B, 34);
      ctx.fillStyle = "#FFFEFC";
      ["Apply in minutes, with no impact to your", "credit score.*"].forEach((line, i) => {
        ctx.fillText(line, pad, y + i * 48);
      });

      const btnY = h - pad - (h === 1080 ? 210 : 260);
      ctx.font = f(D, 32);
      const label = "Apply now";
      const bw = ctx.measureText(label).width + 80;
      const bh = 84;
      ctx.fillStyle = "#F7B334";
      if (ctx.roundRect) {
        ctx.beginPath();
        ctx.roundRect(pad, btnY, bw, bh, 16);
        ctx.fill();
      } else {
        ctx.fillRect(pad, btnY, bw, bh);
      }
      ctx.fillStyle = "#200F51";
      ctx.fillText(label, pad + 40, btnY + 54);

      if (logoImg && logoImg.complete) {
        const lh = 46;
        const lw = lh * (366.43 / 87);
        ctx.drawImage(logoImg, pad, h - pad - 96, lw, lh);
      }

      ctx.font = f(B, 17);
      ctx.fillStyle = "#BCB6CA";
      wrapText(ctx, disclosureShort(), w - pad * 2).forEach((line, i) => {
        ctx.fillText(line, pad, h - pad + 4 + i * 22 - 22);
      });
    });
  }, [channel, defaultChannel, appliedName]);

  drawSocialLatestRef.current = drawSocial;

  // Restore from localStorage on mount (once)
  useEffect(() => {
    let saved = null;
    try {
      saved = JSON.parse(window.localStorage.getItem(STORE_KEY) || "null");
    } catch (e) {
      saved = null;
    }
    if (isDemoSavedData(saved)) {
      saved = saved ? { channel: saved.channel } : null;
    }
    let nextName = "";
    let nextLink = "";
    let nextAppliedName = "Your company";
    let nextAppliedLink = "https://financing.goodleap.plus/your-link";
    let nextGenerated = false;
    let nextChannel = null;

    if (saved && saved.link) {
      nextName = saved.name || "";
      nextLink = saved.link;
      nextAppliedName = saved.name || "Your company";
      nextAppliedLink = saved.link;
      nextGenerated = true;
    }
    if (saved && saved.channel) nextChannel = saved.channel;

    setNameInput(nextName);
    setLinkInput(nextLink);
    setAppliedName(nextAppliedName);
    setAppliedLink(nextAppliedLink);
    setGenerated(nextGenerated);
    if (nextChannel) setChannel(nextChannel);
    hydratedRef.current = true;

    QRCode.toString(nextAppliedLink, {
      type: "svg",
      width: 240,
      margin: 2,
      color: { dark: "#200F51", light: "#FFFFFF" },
    })
      .then((svg) => setQrSvg(svg))
      .catch(() => setQrSvg(""));
  }, []);

  // Redraw social when channel/applied values change
  useEffect(() => {
    if (!hydratedRef.current) return;
    drawSocial();
  }, [drawSocial, appliedLink, appliedName, active]);

  useEffect(() => {
    if (document.fonts && document.fonts.ready) {
      document.fonts.ready.then(() => {
        if (drawSocialLatestRef.current) drawSocialLatestRef.current();
      });
    }
  }, []);

  useEffect(() => () => {
    if (copiedTimerRef.current) clearTimeout(copiedTimerRef.current);
  }, []);

  const requireGenerated = useCallback(() => {
    if (generated) return true;
    window.alert("Add your company name and Financing+ Link at the top, then choose Generate my toolkit.");
    window.scrollTo({ top: 0, behavior: "smooth" });
    return false;
  }, [generated]);

  const scrollToWorkspace = useCallback(() => {
    const el = document.getElementById("workspace");
    if (!el) return;
    let p = el.parentElement;
    let box = null;
    while (p && p !== document.body) {
      const s = getComputedStyle(p);
      if (/auto|scroll/.test(s.overflowY) && p.scrollHeight > p.clientHeight + 8) {
        box = p;
        break;
      }
      p = p.parentElement;
    }
    const top = el.getBoundingClientRect().top;
    if (box) {
      box.scrollTo({
        top: box.scrollTop + top - box.getBoundingClientRect().top - 24,
        behavior: "smooth",
      });
    } else {
      (document.scrollingElement || window).scrollTo({
        top: top + window.scrollY - 24,
        behavior: "smooth",
      });
    }
  }, []);

  const onGenerate = () => {
    const name = nameInput.trim();
    let link = linkInput.trim();
    if (!link || !name) {
      window.alert("Add your company name and your Financing+ Link first.");
      return;
    }
    link = normalizeGoodLink(link);
    setLinkInput(link);
    setAppliedLink(link);
    setAppliedName(name);
    setGenerated(true);
    persist(name, link, channel);
    updateQr(link);
    // Smooth-scroll to GET YOUR BLOCKS after the toolkit is applied
    requestAnimationFrame(() => scrollToWorkspace());
  };

  const selectChannel = (id, { scroll } = {}) => {
    setChannel(id);
    setOpenGuide("");
    persist(nameInput, linkInput, id);
    if (scroll) scrollToWorkspace();
  };

  const copyText = (text, key) => {
    if (!requireGenerated()) return;
    navigator.clipboard.writeText(text).then(() => {
      setCopiedKey(key);
      if (copiedTimerRef.current) clearTimeout(copiedTimerRef.current);
      copiedTimerRef.current = setTimeout(() => setCopiedKey(""), 2200);
    });
  };

  const downloadCanvas = (ref, filename) => {
    if (!requireGenerated()) return;
    const cv = ref.current;
    if (!cv) return;
    try {
      const a = document.createElement("a");
      a.href = cv.toDataURL("image/png");
      a.download = filename;
      a.click();
    } catch (e) {
      window.alert('This browser blocked the image export. Right-click the graphic and choose "Save image as" instead.');
    }
  };

  const onQrSvgDownload = (e) => {
    if (!generated) {
      e.preventDefault();
      requireGenerated();
      return;
    }
  };

  const onQrPng = async () => {
    if (!requireGenerated()) return;
    try {
      const dataUrl = await QRCode.toDataURL(appliedLink, {
        width: 1024,
        margin: 2,
        color: { dark: "#200F51", light: "#FFFFFF" },
      });
      const a = document.createElement("a");
      a.href = dataUrl;
      a.download = "financing-plus-qr.png";
      a.click();
    } catch (e) {
      window.alert("Could not generate the PNG. Try again.");
    }
  };

  const printDoc = printHTML({ appliedName, appliedLink, qrSvg });

  const onPrintOpen = () => {
    if (!requireGenerated()) return;
    const w = window.open("", "_blank");
    if (!w) {
      window.alert('Your browser blocked the print window. Use "Download" and print that file instead.');
      return;
    }
    w.document.write(printDoc);
    w.document.close();
    w.focus();
    setTimeout(() => {
      try {
        w.print();
      } catch (e) {
        /* user can print manually */
      }
    }, 600);
  };

  const onPrintDownload = () => {
    if (!requireGenerated()) return;
    const blob = new Blob([printDoc], { type: "text/html" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = "financing-plus-leave-behind.html";
    a.click();
    setTimeout(() => URL.revokeObjectURL(a.href), 4000);
  };

  const meta = CHANNELS.find((c) => c.id === active) || CHANNELS[0];
  const guides = guidesFor(active);
  const link = appliedLink;
  const name = appliedName;

  const banner = getBannerHTML(link, genOpts);
  const hero = getHeroHTML(link, genOpts);
  const info = getInfoHTML(link, name, genOpts);
  const footer = getFooterHTML(link, genOpts);
  const sigBold = getSigBoldHTML(link);
  const sigMin = getSigMinimalHTML(link);
  const emailTpl = getEmailTplHTML(link, name, genOpts);
  const smsList = smsBodies(link);
  const captions = captionTexts(link);

  let copyAll = null;
  if (active === "website") {
    const all = [banner, hero, info, footer].join("\n\n");
    copyAll = {
      label: copiedKey === "all-web" ? "Copied" : "Copy all four blocks",
      onClick: () => copyText(all, "all-web"),
      copied: copiedKey === "all-web",
    };
  } else if (active === "sms") {
    const all = smsList.map((s) => s.body).join("\n\n");
    copyAll = {
      label: copiedKey === "all-sms" ? "Copied" : "Copy all three",
      onClick: () => copyText(all, "all-sms"),
      copied: copiedKey === "all-sms",
    };
  }

  const qrSvgHref =
    "data:image/svg+xml;base64," +
    (typeof btoa !== "undefined"
      ? btoa(unescape(encodeURIComponent(qrSvg || "")))
      : "");

  const canvasStyle = {
    width: "100%",
    height: "auto",
    display: "block",
    borderRadius: 16,
    background: "#200F51",
  };

  const codeBlocks =
    active === "website"
      ? [
          { key: "banner", title: "Promo bar", hint: "Great at the top of your webpage. ", html: banner, previewWidth: "100%" },
          { key: "hero", title: "Hero section", hint: "Drop it near the top of a financing or service page.", html: hero, previewWidth: "100%" },
          { key: "info", title: "Info Module", hint: "Great for mid-page of your website, with FAQs. ", html: info, previewWidth: "100%" },
          { key: "footer", title: "Footer CTA strip", hint: "Drop this at the bottom of page, above the footer.", html: footer, previewWidth: "100%" },
        ]
      : active === "email-signature"
        ? [
            { key: "sig-bold", title: "Prominent", hint: "Paste below your name and title.", html: sigBold, previewWidth: "440px" },
            { key: "sig-min", title: "Minimal", hint: "Best if your signature is already busy.", html: sigMin, previewWidth: "560px" },
          ]
        : active === "email-template"
          ? [
              {
                key: "email-tpl",
                title: "Financing invitation",
                hint: "Paste into your email tool as HTML, then set the subject line.",
                html: emailTpl,
                previewWidth: "640px",
              },
            ]
          : [];

  return (
    <div className="fp-toolkit">
      {/* 1. Hero ground */}
      <div
        style={{
          background: `url('${HERO_BG}') top center/100% auto no-repeat, linear-gradient(93.207deg,#472BA4 -14%,#200F51 88%)`,
        }}
      >
        {/* 2. Header */}
        <div
          style={{
            padding: "18px 48px",
            display: "flex",
            alignItems: "center",
            gap: 28,
            flexWrap: "wrap",
          }}
        >
          <img src={LOGO_WHITE} alt="GoodLeap" style={{ height: 38, width: "auto", display: "block" }} />
          <span style={{ width: 1, height: 32, background: "rgba(255,255,255,0.18)", display: "block" }} />
          <span
            style={{
              fontFamily: "var(--font-display)",
              fontWeight: 500,
              fontSize: 20,
              lineHeight: 1,
              color: "var(--gl-warm-white)",
            }}
          >
            Financing+ Toolkit
          </span>
          {showPartnerLogo && (
            <div style={{ marginLeft: "auto", display: "flex", alignItems: "center", gap: 20 }}>
              <span
                style={{
                  fontFamily: "var(--font-body)",
                  fontSize: 14,
                  lineHeight: "20px",
                  color: "var(--gl-warm-white)",
                }}
              >
                In partnership with
              </span>
              <img
                src={LOGO_PARTNER}
                alt={partnerName}
                style={{ height: 61, width: 82, display: "block" }}
              />
            </div>
          )}
        </div>

        {/* 3. Hero two-column */}
        <section className="gl-dark-ground" style={{ padding: 0, background: "transparent" }}>
          <div
            style={{
              maxWidth: 1344,
              margin: "0 auto",
              padding: "96px 48px 104px",
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit,minmax(min(100%,440px),1fr))",
              gap: 64,
              alignItems: "start",
            }}
          >
            <div style={{ display: "flex", flexDirection: "column", gap: 32 }}>
              <p className="gl-eyebrow" style={{ color: "var(--accent)" }}>
                GoodLeap Financing+
              </p>
              <h1
                style={{
                  fontFamily: "var(--font-display)",
                  fontWeight: 500,
                  fontSize: "clamp(40px,4.4vw,64px)",
                  lineHeight: 1.02,
                  margin: 0,
                  color: "var(--gl-warm-white)",
                }}
              >
                Everything you need to offer financing,
                <br />
                <span className="gl-gradient-text">in one place</span>
              </h1>
              <p
                style={{
                  fontFamily: "var(--font-body)",
                  fontSize: 24,
                  lineHeight: "32px",
                  margin: 0,
                  maxWidth: 560,
                  color: "var(--gl-warm-white)",
                }}
              >
                Paste your Financing+ Link once. Get brand approved blocks for your website, inbox, phone, print and social media.
              </p>
            </div>

            <div
              style={{
                background: "var(--gl-warm-white)",
                borderRadius: 24,
                padding: 40,
                boxShadow: "var(--shadow-card)",
              }}
            >
              <h2
                style={{
                  fontFamily: "var(--font-display)",
                  fontWeight: 500,
                  fontSize: 32,
                  lineHeight: "36px",
                  margin: "0 0 28px",
                  color: "var(--gl-passion)",
                }}
              >
                Set up your Financing+ toolkit
              </h2>
              <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
                <label style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                  <span
                    style={{
                      fontFamily: "var(--font-body)",
                      fontWeight: 500,
                      fontSize: 14,
                      lineHeight: 1.4,
                      letterSpacing: "0.04em",
                      textTransform: "uppercase",
                      color: "var(--gl-passion)",
                    }}
                  >
                    Company name
                  </span>
                  <span className="fp-setup-input">
                    <input
                      type="text"
                      placeholder="e.g. Your company name"
                      value={nameInput}
                      onChange={(e) => setNameInput(e.target.value)}
                    />
                  </span>
                </label>
                <label style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                  <span
                    style={{
                      fontFamily: "var(--font-body)",
                      fontWeight: 500,
                      fontSize: 14,
                      lineHeight: 1.4,
                      letterSpacing: "0.04em",
                      textTransform: "uppercase",
                      color: "var(--gl-passion)",
                    }}
                    >
                        Your Financing+ Link
                  </span>
                  <span className="fp-setup-input">
                    <input
                      type="url"
                      placeholder="e.g. financing.goodleap.plus/your-link"
                      value={linkInput}
                      onChange={(e) => setLinkInput(e.target.value)}
                    />
                  </span>
                </label>
                <div
                  style={{
                    display: "flex",
                    flexWrap: "wrap",
                    alignItems: "flex-end",
                    justifyContent: "space-between",
                    gap: "16px 24px",
                    width: "100%",
                  }}
                >
                  <button
                    type="button"
                    className={`fp-generate${fieldsReady ? "" : " fp-btn--disabled"}`}
                    onClick={onGenerate}
                    style={{
                      opacity: fieldsReady ? 1 : 0.4,
                      cursor: fieldsReady ? "pointer" : "not-allowed",
                      alignSelf: "flex-end",
                    }}
                  >
                    <span>Get my toolkit</span>
                    <span className="fp-generate__arrow">
                      <ArrowChip />
                    </span>
                  </button>
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "flex-start",
                      justifyContent: "flex-end",
                      gap: 8,
                      alignSelf: "flex-end",
                    }}
                  >
                    <p
                      style={{
                        margin: 0,
                        fontFamily: "var(--font-body)",
                        fontSize: 13,
                        lineHeight: 1.4,
                        color: "var(--gl-passion)",
                        textAlign: "left",
                      }}
                    >
                      Don’t have your Financing+ link?
                    </p>
                    <a
                      href="https://www.goodleap.com/contractor-form/SRS"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="fp-generate"
                      style={{
                        textDecoration: "none",
                        background: "transparent",
                        boxShadow: "inset 0 0 0 1.5px var(--gl-passion)",
                        color: "var(--gl-passion)",
                        alignSelf: "flex-start",
                      }}
                    >
                      <span>Request yours here</span>
                      <span className="fp-generate__arrow">
                        <ArrowChip />
                      </span>
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
      </div>

      {/* 4. How it works */}
      <section style={{ maxWidth: 1344, margin: "0 auto", padding: "104px 48px 0" }}>
        <p className="gl-eyebrow" style={{ color: "var(--gl-violet)", marginBottom: 24, fontSize: 15 }}>
          How it works
        </p>
        <h2
          style={{
            fontFamily: "var(--font-display)",
            fontWeight: 500,
            fontSize: "clamp(32px,3.6vw,48px)",
            lineHeight: 1.08,
            margin: "0 0 48px",
            color: "var(--gl-passion)",
            maxWidth: 820,
          }}
        >
          One link, four steps, every channel you already use
        </h2>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit,minmax(240px,1fr))",
            gap: 24,
          }}
        >
          {[
            { n: "1", t: "Get your Financing+ Link", b: "Your dedicated link, issued at onboarding." },
            {
              n: "2",
              t: "Put it everywhere",
              b: "Website, signature, texts, email, QR, print, social. One experience across all of them.",
            },
            {
              n: "3",
              t: "Homeowners apply",
              b: "A short application, a decision in minutes, and no friction in between.",
            },
            {
              n: "4",
              t: `It lands in ${hubName}`,
              b: "Submissions, approvals and next steps, tracked by your team in one place.",
            },
          ].map((step) => (
            <div
              key={step.n}
              style={{
                background: "var(--gl-blush)",
                borderRadius: 24,
                padding: 32,
                display: "flex",
                flexDirection: "column",
                gap: 12,
              }}
            >
              <span
                style={{
                  fontFamily: "var(--font-display)",
                  fontWeight: 500,
                  fontSize: 40,
                  lineHeight: "44px",
                  color: "var(--gl-passion)",
                }}
              >
                {step.n}
              </span>
              <h3
                style={{
                  fontFamily: "var(--font-display)",
                  fontWeight: 500,
                  fontSize: 24,
                  lineHeight: "28px",
                  margin: 0,
                  color: "var(--gl-passion)",
                }}
              >
                {step.t}
              </h3>
              <p
                style={{
                  fontFamily: "var(--font-body)",
                  fontSize: 16,
                  lineHeight: "22px",
                  margin: 0,
                  color: "var(--gl-warm-black)",
                }}
              >
                {step.b}
              </p>
            </div>
          ))}
        </div>
      </section>

      {/* 5. Channel picker */}
      <section style={{ maxWidth: 1344, margin: "0 auto", padding: "104px 48px" }}>
        <p className="gl-eyebrow" style={{ color: "var(--gl-violet)", marginBottom: 24, fontSize: 15 }}>
          Pick a channel
        </p>
        <h2
          style={{
            fontFamily: "var(--font-display)",
            fontWeight: 500,
            fontSize: "clamp(32px,3.6vw,48px)",
            lineHeight: 1.08,
            margin: "0 0 16px",
            color: "var(--gl-passion)",
            maxWidth: 900,
          }}
        >
          Seven ways to say the same thing well
        </h2>
        <p
          style={{
            fontFamily: "var(--font-body)",
            fontSize: 20,
            lineHeight: "26px",
            margin: "0 0 48px",
            maxWidth: 900,
            color: "var(--gl-warm-black)",
          }}
        >
          Tap on a communication channel below to receive ready-to-use and approved resources.&nbsp;&nbsp;
        </p>

        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit,minmax(260px,1fr))",
            gridAutoFlow: "dense",
            gap: 24,
          }}
        >
          <div
            className="fp-channel-tile--featured"
            style={{
              gridColumn: "span 2",
              gridRow: "span 2",
              background: "var(--gradient-purple)",
              borderRadius: 24,
              padding: 40,
              display: "flex",
              flexDirection: "column",
              gap: 12,
              justifyContent: "center",
            }}
          >
            <img
              src={LOGO_HUB}
              alt={hubName}
              style={{ width: 145, height: 58, alignSelf: "flex-start", display: "block", marginBottom: 8 }}
            />
            <h3
              style={{
                fontFamily: "var(--font-display)",
                fontWeight: 500,
                fontSize: 32,
                lineHeight: "36px",
                margin: 0,
                color: "var(--gl-warm-white)",
              }}
            >
              Every channel lands in the same place
            </h3>
            <p
              style={{
                fontFamily: "var(--font-body)",
                fontSize: 18,
                lineHeight: "26px",
                margin: 0,
                color: "var(--gl-lilac)",
                maxWidth: 460,
              }}
            >
              From email to social to text, your customers can easily connect with you, all inside {hubName}.
            </p>
          </div>

          {[
            { id: "email-signature", icon: "mail", title: "Email signature", body: "A financing offer on every message you already send." },
            { id: "email-template", icon: "paperplane", title: "Email template", body: "A full invitation email for your marketing outreach." },
            { id: "sms", icon: "chat", title: "Text messages", body: "Three messages: outreach, post-estimate, and reminder." },
            { id: "qr", icon: "camera", title: "QR code", body: "Scannable, downloadable, scoped to your link." },
          ].map((tile) => (
            <div
              key={tile.id}
              className="fp-channel-tile"
              role="button"
              tabIndex={0}
              onClick={() => selectChannel(tile.id, { scroll: true })}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") selectChannel(tile.id, { scroll: true });
              }}
              style={{
                background: "var(--gl-blush)",
                borderRadius: 24,
                padding: 32,
                display: "flex",
                flexDirection: "column",
                gap: 12,
                cursor: "pointer",
              }}
            >
              <img src={ICON(tile.icon)} alt="" style={{ width: 48, height: 48, display: "block" }} />
              <h3
                style={{
                  fontFamily: "var(--font-display)",
                  fontWeight: 500,
                  fontSize: 24,
                  lineHeight: "28px",
                  margin: 0,
                  color: "var(--gl-passion)",
                }}
              >
                {tile.title}
              </h3>
              <p
                style={{
                  fontFamily: "var(--font-body)",
                  fontSize: 16,
                  lineHeight: "22px",
                  margin: 0,
                  color: "var(--gl-warm-black)",
                }}
              >
                {tile.body}
              </p>
            </div>
          ))}

          <div
            className="fp-channel-tile"
            role="button"
            tabIndex={0}
            onClick={() => selectChannel("website", { scroll: true })}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") selectChannel("website", { scroll: true });
            }}
            style={{
              gridColumn: "span 2",
              background: "var(--gl-blush)",
              borderRadius: 24,
              padding: 40,
              display: "flex",
              flexDirection: "column",
              gap: 16,
              cursor: "pointer",
            }}
          >
            <img src={ICON("global")} alt="" style={{ width: 64, height: 64, display: "block" }} />
            <h3
              style={{
                fontFamily: "var(--font-display)",
                fontWeight: 500,
                fontSize: 32,
                lineHeight: "36px",
                margin: 0,
                color: "var(--gl-passion)",
              }}
            >
              Website blocks
            </h3>
            <p
              style={{
                fontFamily: "var(--font-body)",
                fontSize: 18,
                lineHeight: "26px",
                margin: 0,
                color: "var(--gl-warm-black)",
                maxWidth: 420,
              }}
            >
              Paste-in HTML for your website with a hero, a promo bar, an explainer module, and a footer strip.
            </p>
            <span
              style={{
                marginTop: "auto",
                fontFamily: "var(--font-display)",
                fontWeight: 500,
                fontSize: 16,
                lineHeight: "20px",
                color: "var(--gl-grape)",
              }}
            >
              4 blocks →
            </span>
          </div>

          {[
            { id: "social", icon: "design", title: "Social posts", body: "Square and portrait graphics, plus captions." },
            { id: "print", icon: "notebook", title: "Leave-behind flyer", body: "A one-page sheet with your QR code, ready to print." },
          ].map((tile) => (
            <div
              key={tile.id}
              className="fp-channel-tile"
              role="button"
              tabIndex={0}
              onClick={() => selectChannel(tile.id, { scroll: true })}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") selectChannel(tile.id, { scroll: true });
              }}
              style={{
                background: "var(--gl-blush)",
                borderRadius: 24,
                padding: 32,
                display: "flex",
                flexDirection: "column",
                gap: 12,
                cursor: "pointer",
              }}
            >
              <img src={ICON(tile.icon)} alt="" style={{ width: 48, height: 48, display: "block" }} />
              <h3
                style={{
                  fontFamily: "var(--font-display)",
                  fontWeight: 500,
                  fontSize: 24,
                  lineHeight: "28px",
                  margin: 0,
                  color: "var(--gl-passion)",
                }}
              >
                {tile.title}
              </h3>
              <p
                style={{
                  fontFamily: "var(--font-body)",
                  fontSize: 16,
                  lineHeight: "22px",
                  margin: 0,
                  color: "var(--gl-warm-black)",
                }}
              >
                {tile.body}
              </p>
            </div>
          ))}
        </div>
      </section>

      {/* 6. Workspace */}
      <div style={{ background: "var(--gl-passion)" }}>
        <div id="workspace" className="gl-dark-ground" style={{ padding: "20px 0 0", background: "transparent" }}>
          <div style={{ maxWidth: 1344, margin: "0 auto", padding: "0 48px 96px" }}>
            <p className="gl-eyebrow" style={{ color: "var(--gl-lilac)", marginBottom: 24, fontSize: 15 }}>
              GET YOUR BLOCKS
            </p>

            <div
              data-tabrow=""
              style={{
                display: "flex",
                flexWrap: "nowrap",
                alignItems: "flex-end",
                gap: 32,
                marginBottom: 40,
                overflowX: "auto",
                borderBottom: "1px solid rgba(255,255,255,0.18)",
              }}
            >
              {CHANNELS.map((c) => (
                <button
                  key={c.id}
                  type="button"
                  className={`fp-chip${c.id === active ? " fp-chip--active" : ""}`}
                  onClick={() => selectChannel(c.id)}
                >
                  {c.label}
                </button>
              ))}
            </div>

            <div
              style={{
                display: "flex",
                alignItems: "flex-end",
                justifyContent: "space-between",
                gap: 32,
                flexWrap: "wrap",
                marginBottom: 40,
              }}
            >
              <div style={{ maxWidth: 760 }}>
                <h2
                  style={{
                    fontFamily: "var(--font-display)",
                    fontWeight: 500,
                    fontSize: 40,
                    lineHeight: "44px",
                    margin: "0 0 12px",
                    color: "var(--gl-warm-white)",
                  }}
                >
                  {meta.title}
                </h2>
                <p
                  style={{
                    fontFamily: "var(--font-body)",
                    fontSize: 20,
                    lineHeight: "26px",
                    margin: 0,
                    color: "var(--gl-lilac)",
                  }}
                >
                  {meta.blurb}
                </p>
              </div>
              {copyAll && (
                <button
                  type="button"
                  className={btnClass(generated, copyAll.copied, "secondary")}
                  onClick={copyAll.onClick}
                >
                  {copyAll.label}
                </button>
              )}
            </div>

            {/* Guides */}
            {guides.length > 0 && (
              <div style={{ display: "flex", flexWrap: "wrap", gap: 16, marginBottom: 16 }}>
                {guides.map((g) => {
                  const open = openGuide === g.key;
                  return (
                    <div key={g.key}>
                      <button
                        type="button"
                        className={`fp-guide-btn${open ? " fp-guide-btn--open" : ""}`}
                        onClick={() => setOpenGuide(open ? "" : g.key)}
                      >
                        {g.label}
                      </button>
                      {open && (
                        <div
                          style={{
                            background: "var(--gl-blush)",
                            borderRadius: 24,
                            padding: 32,
                            marginTop: 16,
                          }}
                        >
                          <h4
                            style={{
                              fontFamily: "var(--font-display)",
                              fontWeight: 500,
                              fontSize: 24,
                              lineHeight: "28px",
                              margin: "0 0 16px",
                              color: "var(--gl-passion)",
                            }}
                          >
                            {g.title}
                          </h4>
                          <ol
                            style={{
                              margin: 0,
                              paddingLeft: 22,
                              fontFamily: "var(--font-body)",
                              fontSize: 16,
                              lineHeight: "26px",
                              color: "var(--gl-warm-black)",
                            }}
                          >
                            {g.steps.map((step) => (
                              <li key={step} style={{ marginBottom: 6 }}>
                                {step}
                              </li>
                            ))}
                          </ol>
                          <p
                            style={{
                              fontFamily: "var(--font-body)",
                              fontSize: 14,
                              lineHeight: "20px",
                              margin: "16px 0 0",
                              color: "var(--text-muted)",
                            }}
                          >
                            {g.tip}
                          </p>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}

            {active === "sms" && (
              <p
                style={{
                  fontFamily: "var(--font-body)",
                  fontSize: 14,
                  lineHeight: "20px",
                  margin: "24px 0 0",
                  color: "var(--gl-lilac-quiet)",
                  maxWidth: 820,
                }}
              >
                Every text must go out with the recipient&apos;s consent. The sender is solely responsible for TCPA compliance.
              </p>
            )}

            {/* Code blocks */}
            {codeBlocks.map((block) => (
              <div
                key={block.key}
                style={{
                  background: "var(--gl-white)",
                  borderRadius: 24,
                  marginTop: 32,
                  overflow: "hidden",
                  boxShadow: "var(--shadow-card)",
                }}
              >
                <div
                  style={{
                    padding: "28px 32px",
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    gap: 16,
                    flexWrap: "wrap",
                  }}
                >
                  <h3
                    style={{
                      fontFamily: "var(--font-display)",
                      fontWeight: 500,
                      fontSize: 24,
                      lineHeight: "28px",
                      margin: 0,
                      color: "var(--gl-passion)",
                    }}
                  >
                    {block.title}
                  </h3>
                </div>
                <div style={{ padding: 32, background: "var(--gl-light-asphalt)" }}>
                  <div style={{ maxWidth: block.previewWidth, margin: "0 auto", borderRadius: 16, overflow: "hidden" }}>
                    <PreviewHtml html={block.html} />
                  </div>
                </div>
                <div style={{ padding: "24px 32px", display: "flex", alignItems: "center", gap: 16, flexWrap: "wrap" }}>
                  <button
                    type="button"
                    className={btnClass(generated, copiedKey === block.key)}
                    onClick={() => copyText(block.html, block.key)}
                  >
                    {copiedKey === block.key ? "Copied" : "Copy HTML"}
                  </button>
                  <span
                    style={{
                      fontFamily: "var(--font-body)",
                      fontSize: 14,
                      lineHeight: "20px",
                      color: "var(--text-muted)",
                    }}
                  >
                    {block.hint}
                  </span>
                </div>
              </div>
            ))}

            {/* SMS */}
            {active === "sms" &&
              smsList.map((sms, i) => {
                const key = `sms${i}`;
                return (
                  <div
                    key={key}
                    style={{
                      background: "var(--gl-white)",
                      borderRadius: 24,
                      marginTop: 32,
                      overflow: "hidden",
                      boxShadow: "var(--shadow-card)",
                    }}
                  >
                    <div style={{ padding: "28px 32px" }}>
                      <h3
                        style={{
                          fontFamily: "var(--font-display)",
                          fontWeight: 500,
                          fontSize: 24,
                          lineHeight: "28px",
                          margin: 0,
                          color: "var(--gl-passion)",
                        }}
                      >
                        {sms.title}
                      </h3>
                    </div>
                    <div style={{ padding: 32, background: "var(--gl-light-asphalt)" }}>
                      <div
                        style={{
                          maxWidth: 440,
                          background: "var(--gl-blush)",
                          borderRadius: 24,
                          padding: 24,
                        }}
                      >
                        <p
                          style={{
                            fontFamily: "var(--font-body)",
                            fontSize: 16,
                            lineHeight: "24px",
                            margin: 0,
                            color: "var(--gl-warm-black)",
                          }}
                        >
                          {sms.body}
                        </p>
                      </div>
                    </div>
                    <div
                      style={{
                        padding: "24px 32px 32px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "flex-start",
                        gap: 16,
                        flexWrap: "wrap",
                      }}
                    >
                      <button
                        type="button"
                        className={btnClass(generated, copiedKey === key)}
                        onClick={() => copyText(sms.body, key)}
                      >
                        {copiedKey === key ? "Copied" : "Copy text"}
                      </button>
                    </div>
                  </div>
                );
              })}

            {/* QR */}
            {active === "qr" && (
              <div
                style={{
                  background: "var(--gl-white)",
                  borderRadius: 24,
                  marginTop: 32,
                  overflow: "hidden",
                  boxShadow: "var(--shadow-card)",
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fit,minmax(min(100%,360px),1fr))",
                  gap: 0,
                }}
              >
                <div style={{ padding: 40 }}>
                  <h3
                    style={{
                      fontFamily: "var(--font-display)",
                      fontWeight: 500,
                      fontSize: 24,
                      lineHeight: "28px",
                      margin: "0 0 16px",
                      color: "var(--gl-passion)",
                    }}
                  >
                    Where to use it
                  </h3>
                  <ul
                    style={{
                      margin: "0 0 24px",
                      paddingLeft: 22,
                      fontFamily: "var(--font-body)",
                      fontSize: 16,
                      lineHeight: "26px",
                      color: "var(--gl-warm-black)",
                    }}
                  >
                    <li>Printed estimates and proposals handed over on site</li>
                    <li>Brochures, flyers and leave-behinds</li>
                    <li>Yard signs at job sites</li>
                    <li>Vehicle wraps and door magnets</li>
                    <li>Slides shown during a consultation</li>
                  </ul>
                  <p
                    style={{
                      fontFamily: "var(--font-body)",
                      fontSize: 14,
                      lineHeight: "20px",
                      margin: "0 0 8px",
                      color: "var(--text-muted)",
                    }}
                  >
                    This code is not the same file you got at onboarding, but both point at the same Financing+ Link — either one works.
                  </p>
                  <p
                    style={{
                      fontFamily: "var(--font-body)",
                      fontSize: 14,
                      lineHeight: "20px",
                      margin: 0,
                      color: "var(--text-muted)",
                    }}
                  >
                    Scan it with your own phone before it goes to print.
                  </p>
                </div>
                <div
                  style={{
                    background: "var(--gl-light-asphalt)",
                    padding: 40,
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: 24,
                  }}
                >
                  <div
                    style={{
                      width: 280,
                      height: 280,
                      background: "var(--gl-white)",
                      borderRadius: 16,
                      padding: 20,
                      boxShadow: "var(--shadow-raised)",
                    }}
                  >
                    <div
                      style={{ width: "100%", height: "100%" }}
                      dangerouslySetInnerHTML={{
                        __html: (qrSvg || "").replace(
                          "<svg ",
                          '<svg style="width:100%;height:100%;display:block" '
                        ),
                      }}
                    />
                  </div>
                  <div style={{ display: "flex", gap: 12, flexWrap: "wrap", justifyContent: "center" }}>
                    <a
                      href={qrSvgHref}
                      download="financing-plus-qr.svg"
                      onClick={onQrSvgDownload}
                      className={btnClass(generated, false)}
                    >
                      Download SVG
                    </a>
                    <button
                      type="button"
                      className={btnClass(generated, false, "secondary")}
                      onClick={onQrPng}
                    >
                      Download PNG
                    </button>
                  </div>
                  <p
                    style={{
                      fontFamily: "var(--font-body)",
                      fontSize: 12,
                      lineHeight: "16px",
                      margin: 0,
                      color: "var(--text-muted)",
                      textAlign: "center",
                    }}
                  >
                    SVG for print, PNG for slides and screens.
                  </p>
                </div>
              </div>
            )}

            {/* Social */}
            {active === "social" && (
              <>
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "repeat(auto-fit,minmax(min(100%,380px),1fr))",
                    gap: 32,
                    marginTop: 32,
                  }}
                >
                  <div
                    style={{
                      background: "var(--gl-white)",
                      borderRadius: 24,
                      overflow: "hidden",
                      boxShadow: "var(--shadow-card)",
                    }}
                  >
                    <div style={{ padding: "28px 32px" }}>
                      <h3
                        style={{
                          fontFamily: "var(--font-display)",
                          fontWeight: 500,
                          fontSize: 24,
                          lineHeight: "28px",
                          margin: 0,
                          color: "var(--gl-passion)",
                        }}
                      >
                        Square — 1080 × 1080
                      </h3>
                      <p
                        style={{
                          fontFamily: "var(--font-body)",
                          fontSize: 14,
                          lineHeight: "20px",
                          margin: "8px 0 0",
                          color: "var(--text-muted)",
                        }}
                      >
                        Instagram, Facebook, or LinkedIn compatible.
                      </p>
                    </div>
                    <div style={{ padding: "0 32px 24px" }}>
                      <canvas ref={squareRef} width={1080} height={1080} style={canvasStyle} />
                    </div>
                    <div style={{ padding: "0 32px 28px" }}>
                      <button
                        type="button"
                        className={btnClass(generated, false)}
                        onClick={() => downloadCanvas(squareRef, "financing-plus-square.png")}
                      >
                        Download PNG
                      </button>
                    </div>
                  </div>
                  <div
                    style={{
                      background: "var(--gl-white)",
                      borderRadius: 24,
                      overflow: "hidden",
                      boxShadow: "var(--shadow-card)",
                    }}
                  >
                    <div style={{ padding: "28px 32px" }}>
                      <h3
                        style={{
                          fontFamily: "var(--font-display)",
                          fontWeight: 500,
                          fontSize: 24,
                          lineHeight: "28px",
                          margin: 0,
                          color: "var(--gl-passion)",
                        }}
                      >
                        Portrait — 1080 × 1350
                      </h3>
                      <p
                        style={{
                          fontFamily: "var(--font-body)",
                          fontSize: 14,
                          lineHeight: "20px",
                          margin: "8px 0 0",
                          color: "var(--text-muted)",
                        }}
                      >
                        Perfect for Stories or Reels.
                      </p>
                    </div>
                    <div style={{ padding: "0 32px 24px" }}>
                      <canvas ref={portraitRef} width={1080} height={1350} style={canvasStyle} />
                    </div>
                    <div style={{ padding: "0 32px 28px" }}>
                      <button
                        type="button"
                        className={btnClass(generated, false)}
                        onClick={() => downloadCanvas(portraitRef, "financing-plus-portrait.png")}
                      >
                        Download PNG
                      </button>
                    </div>
                  </div>
                </div>
                <div
                  style={{
                    background: "var(--gl-white)",
                    borderRadius: 24,
                    marginTop: 32,
                    padding: 40,
                    boxShadow: "var(--shadow-card)",
                  }}
                >
                  <h3
                    style={{
                      fontFamily: "var(--font-display)",
                      fontWeight: 500,
                      fontSize: 24,
                      lineHeight: "28px",
                      margin: "0 0 24px",
                      color: "var(--gl-passion)",
                    }}
                  >
                    Captions
                  </h3>
                  <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
                    {captions.map((text, i) => {
                      const key = `cap${i}`;
                      return (
                        <div
                          key={key}
                          style={{
                            display: "flex",
                            gap: 24,
                            alignItems: "flex-start",
                            background: "var(--gl-blush)",
                            borderRadius: 16,
                            padding: 24,
                          }}
                        >
                          <p
                            style={{
                              fontFamily: "var(--font-body)",
                              fontSize: 16,
                              lineHeight: "24px",
                              margin: 0,
                              flex: 1,
                              color: "var(--gl-warm-black)",
                            }}
                          >
                            {text}
                          </p>
                          <button
                            type="button"
                            className={btnClass(generated, copiedKey === key, "secondary")}
                            onClick={() => copyText(text, key)}
                          >
                            {copiedKey === key ? "Copied" : "Copy"}
                          </button>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </>
            )}

            {/* Print */}
            {active === "print" && (
              <div
                style={{
                  background: "var(--gl-white)",
                  borderRadius: 24,
                  marginTop: 32,
                  overflow: "hidden",
                  boxShadow: "var(--shadow-card)",
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fit,minmax(min(100%,420px),1fr))",
                }}
              >
                <div style={{ padding: 40 }}>
                  <h3
                    style={{
                      fontFamily: "var(--font-display)",
                      fontWeight: 500,
                      fontSize: 24,
                      lineHeight: "28px",
                      margin: "0 0 16px",
                      color: "var(--gl-passion)",
                    }}
                  >
                    A sheet you can hand over
                  </h3>
                  <p
                    style={{
                      fontFamily: "var(--font-body)",
                      fontSize: 16,
                      lineHeight: "24px",
                      margin: "0 0 20px",
                      color: "var(--gl-warm-black)",
                    }}
                  >
                    Your company name and QR code are already on it. Print it on plain 8.5 x 11 letter paper, leave it on the counter, or staple it to the estimate.
                  </p>
                  <ul
                    style={{
                      margin: "0 0 24px",
                      paddingLeft: 22,
                      fontFamily: "var(--font-body)",
                      fontSize: 16,
                      lineHeight: "26px",
                      color: "var(--gl-warm-black)",
                    }}
                  >
                    <li>Print at 100% scale — do not fit to page</li>
                    <li>Scan the code once before you print a stack</li>
                    <li>Save it as a PDF from the print dialog to email instead</li>
                  </ul>
                  <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
                    <button type="button" className={btnClass(generated, false)} onClick={onPrintOpen}>
                      Open print view
                    </button>
                    <button
                      type="button"
                      className={btnClass(generated, false, "secondary")}
                      onClick={onPrintDownload}
                    >
                      <span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
                        <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
                          <path
                            d="M8 1.5V10.5M8 10.5L4.5 7M8 10.5L11.5 7M2 12.5V13.5C2 13.9 2.35 14.5 3 14.5H13C13.65 14.5 14 13.9 14 13.5V12.5"
                            stroke="currentColor"
                            strokeWidth="1.5"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                        Download
                      </span>
                    </button>
                  </div>
                </div>
                <div
                  style={{
                    background: "var(--gl-light-asphalt)",
                    padding: 40,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <div
                    style={{
                      width: 408,
                      height: 528,
                      overflow: "hidden",
                      boxShadow: "var(--shadow-overlay)",
                      background: "#fff",
                    }}
                  >
                    <iframe
                      title="Leave-behind preview"
                      srcDoc={printDoc}
                      style={{
                        width: 816,
                        height: 1056,
                        border: "none",
                        transform: "scale(0.5)",
                        transformOrigin: "top left",
                      }}
                    />
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* 7. Brand guidelines */}
        <section style={{ background: "var(--gl-light-asphalt)" }}>
          <div style={{ maxWidth: 1344, margin: "0 auto", padding: "104px 48px" }}>
            <div
              style={{
                borderRadius: 24,
                padding: 48,
                display: "flex",
                flexDirection: "column",
                alignItems: "flex-start",
                gap: 24,
                backgroundColor: "var(--gl-blush)",
              }}
            >
              <div style={{ maxWidth: 640 }}>
                <h3
                  style={{
                    fontFamily: "var(--font-display)",
                    fontWeight: 500,
                    fontSize: 32,
                    lineHeight: "36px",
                    margin: "0 0 12px",
                    color: "var(--gl-passion)",
                  }}
                >
                  Using the GoodLeap brand
                </h3>
                <p
                  style={{
                    fontFamily: "var(--font-body)",
                    fontSize: 18,
                    lineHeight: "26px",
                    margin: "0 0 8px",
                    color: "var(--gl-warm-black)",
                  }}
                >
                  Every block here is already approved. If you need logo files, usage rules or anything beyond this toolkit, start with the media kit.
                </p>
                <p
                  style={{
                    fontFamily: "var(--font-body)",
                    fontSize: 14,
                    lineHeight: "20px",
                    margin: 0,
                    color: "var(--text-muted)",
                  }}
                >
                  Alternate logo uses and custom requests:{" "}
                  <a href="mailto:marketing@goodleap.com">marketing@goodleap.com</a>
                </p>
              </div>
              <a
                href="https://www.goodleap.com/newsroom"
                target="_blank"
                rel="noopener noreferrer"
                className="fp-generate"
                style={{ textDecoration: "none" }}
              >
                <span>Brand guidelines &amp; media kit</span>
                <span className="fp-generate__arrow">
                  <ArrowChip />
                </span>
              </a>
            </div>
          </div>
        </section>

        {/* 8. Footer */}
        <div className="gl-dark-ground" style={{ padding: "56px 48px", background: "transparent" }}>
          <div
            style={{
              maxWidth: 1344,
              margin: "0 auto",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 40,
              flexWrap: "wrap",
            }}
          >
            <img src={LOGO_WHITE} alt="GoodLeap" style={{ height: 36, width: "auto", display: "block" }} />
            <p
              style={{
                fontFamily: "var(--font-display)",
                fontWeight: 500,
                fontSize: 24,
                lineHeight: "28px",
                margin: 0,
                color: "var(--gl-warm-white)",
              }}
            >
              One link. More approvals. Stronger together.
            </p>
            {showPartnerLogo && (
              <img
                src={LOGO_PARTNER}
                alt={partnerName}
                style={{ height: 62, width: 85, display: "block" }}
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default FinancingPlus;
